(function () {
  function formatMoney(cents, currency) {
    const amount = (cents / 100).toFixed(2);
    if (currency === 'USD') return '$' + amount + ' USD';
    return amount + ' ' + (currency || '');
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  const MOUNT_ICONS = {
    inside:
      '<svg viewBox="0 0 64 48" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="12" y="8" width="40" height="36" rx="1"/><rect x="18" y="14" width="28" height="24" fill="currentColor" opacity="0.15"/></svg>',
    outside:
      '<svg viewBox="0 0 64 48" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="8" y="4" width="48" height="40" rx="1"/><rect x="14" y="10" width="36" height="28" fill="currentColor" opacity="0.15"/></svg>',
  };

  const FRACTION_OPTIONS = [
    { value: '0', label: '0/0"' },
    { value: '0.125', label: '1/8"' },
    { value: '0.25', label: '1/4"' },
    { value: '0.375', label: '3/8"' },
    { value: '0.5', label: '1/2"' },
    { value: '0.625', label: '5/8"' },
    { value: '0.75', label: '3/4"' },
    { value: '0.875', label: '7/8"' },
  ];

  class ShadeConfigurator {
    constructor(root, gallery) {
      this.root = root;
      this.gallery = gallery || null;
      this.form = root.querySelector('[data-shade-form]');
      this.configContainer = root.querySelector('[data-config-fields]');
      this.priceEl = root.querySelector('[data-config-price]');
      this.variantPrice = parseInt(root.dataset.variantPrice, 10) || 0;
      this.currency = root.dataset.currency || 'USD';
      this.sectionId = root.dataset.sectionId || '';
      this.state = {
        width: '',
        height: '',
        depth: '',
        width_whole: '',
        width_frac: '',
        height_whole: '',
        height_frac: '',
      };
      this.selections = {};
      this.config = null;
      this.init();
    }

    async init() {
      try {
        await this.loadConfig();
        if (!this.config) return;
        this.renderFields();
        this.setDefaults();
        this.applyDimensionDefaults();
        this.bindEvents();
        this.updatePrice();
        this.updateGalleryFromSelection();
        if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
      } catch (err) {
        console.error('Shade configurator init failed', err);
        if (this.configContainer) {
          this.configContainer.innerHTML =
            '<p class="sl-config__help">Failed to load configuration. Please refresh the page.</p>';
        }
      }
    }

    async loadConfig() {
      const script = this.root.querySelector('[data-shade-config-json]');
      const inline = script?.textContent?.trim();
      if (inline) {
        try {
          this.config = JSON.parse(inline);
        } catch (e) {
          console.warn('Inline shade config parse failed', e);
        }
      }
      if (!this.config) {
        const url = this.root.dataset.defaultConfigUrl;
        if (!url) {
          this.configContainer.innerHTML = '<p class="sl-config__help">Configuration not available for this product.</p>';
          return;
        }
        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error('HTTP ' + res.status);
          this.config = await res.json();
        } catch (e) {
          console.error('Failed to load shade config', e);
          this.configContainer.innerHTML = '<p class="sl-config__help">Failed to load configuration. Please refresh the page.</p>';
          return;
        }
      }
      if (this.config.dimension_style === 'apo_inch' && this.config.inch_pricing_asset) {
        const inchUrl = this.root.dataset.inchPricingUrl;
        if (inchUrl) {
          try {
            const res = await fetch(inchUrl);
            if (res.ok) Object.assign(this.config, await res.json());
          } catch (e) {
            console.warn('Failed to load inch pricing', e);
          }
        }
      }
      this.resolveSwatchAssets();
    }

    resolveSwatchAssets() {
      const base = this.root.dataset.shadeAssetsBase;
      if (!base) return;
      (this.config.fields || []).forEach((field) => {
        (field.choices || []).forEach((c) => {
          if (c.image_asset) c.image = base + c.image_asset;
        });
      });
    }

    hasInlineDimensionFields() {
      return (this.config.fields || []).some((f) => f.type === 'dimension_select');
    }

    renderFields() {
      const unit = this.config.dimension_unit || 'in';
      let html = '';
      const fields = this.config.fields || [];
      const dimAfter = this.config.dimensions_after || null;
      const inlineDims = this.hasInlineDimensionFields();
      let dimsRendered = inlineDims;
      let i = 0;
      while (i < fields.length) {
        const field = fields[i];
        if (field.type === 'dimension_select' && field.id === 'width_whole') {
          html += this.renderInlineDimensionGroup(unit);
          while (
            i < fields.length &&
            (fields[i].type === 'dimension_select' || fields[i].type === 'dimension_input')
          ) {
            i++;
          }
          continue;
        }
        if (field.type === 'dimension_select' || field.type === 'dimension_input') {
          i++;
          continue;
        }
        if (!dimsRendered && dimAfter && field.id === dimAfter) {
          html += this.renderDimensions(unit);
          dimsRendered = true;
        }
        html += this.renderField(field);
        i++;
      }
      if (!dimsRendered && this.config.dimensions) {
        html = this.renderDimensions(unit) + html;
      }
      this.configContainer.innerHTML = html;
    }

    usesWholeFractionDims() {
      const style = this.config.dimension_style;
      if (style === 'number') return false;
      if (style === 'whole_fraction' || style === 'apo_inch') return true;
      const d = this.config.dimensions || {};
      return !!(d.width && d.height);
    }

    usesApoInchPricing() {
      return this.config.dimension_style === 'apo_inch' && (this.config.width_whole_options || []).length > 0;
    }

    renderWholeInchOptions(min, max) {
      let html = '<option value="">-- Please Select --</option>';
      for (let i = min; i <= max; i++) {
        html += `<option value="${i}">${i}</option>`;
      }
      return html;
    }

    renderApoWholeOptions(axis) {
      const opts = this.config[axis + '_whole_options'] || [];
      let html = '<option value="">-- Please Select --</option>';
      opts.forEach((o) => {
        let label = `${o.inch}"`;
        if (o.addon > 0) label += ` (+${formatMoney(o.addon, this.currency)})`;
        html += `<option value="${o.inch}" data-addon="${o.addon}">${escapeHtml(label)}</option>`;
      });
      return html;
    }

    renderFractionOptions() {
      let html = '<option value="">-- Please Select --</option>';
      FRACTION_OPTIONS.forEach((f) => {
        html += `<option value="${f.value}">${escapeHtml(f.label)}</option>`;
      });
      return html;
    }

    renderDimensionSelect(axis, dim, unit, sid) {
      const min = dim.min;
      const max = dim.max;
      const label = dim.label || axis;
      const req = dim.required !== false ? '<span class="sl-config__req">*</span>' : '';
      const axisCap = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
      return `<div class="sl-config__dim-row">
        <div class="sl-config__dim">
          <label for="shade-${axis}-whole-${sid}">${escapeHtml(axisCap)} (Whole Inches) ${req}</label>
          <select id="shade-${axis}-whole-${sid}" data-dim-whole="${axis}" class="sl-config__dim-select">
            ${this.usesApoInchPricing() ? this.renderApoWholeOptions(axis) : this.renderWholeInchOptions(min, max)}
          </select>
        </div>
        <div class="sl-config__dim">
          <label for="shade-${axis}-frac-${sid}">${escapeHtml(axisCap)} (Fractional Inches) ${req}</label>
          <select id="shade-${axis}-frac-${sid}" data-dim-frac="${axis}" class="sl-config__dim-select">
            ${this.renderFractionOptions()}
          </select>
        </div>
      </div>`;
    }

    renderDimensions(unit) {
      const d = this.config.dimensions || {};
      if (!d.width && !d.height) return '';
      const sid = this.sectionId;
      let inner = '';

      if (this.usesWholeFractionDims()) {
        if (d.width) inner += this.renderDimensionSelect('width', d.width, unit, sid);
        if (d.height) inner += this.renderDimensionSelect('height', d.height, unit, sid);
        if (d.depth) {
          inner += `<div class="sl-config__dim sl-config__dim--depth">
            <label for="shade-depth-${sid}">${escapeHtml(d.depth.label || 'Depth')}</label>
            <input type="number" id="shade-depth-${sid}" data-dim="depth"
              min="${d.depth.min}" max="${d.depth.max}" step="${d.depth.step || 0.125}"
              placeholder="Optional">
            <span class="sl-config__dim-unit">${unit}</span>
          </div>`;
        }
        return `<div class="sl-config__field" data-field-wrap="dimensions">
          <label class="sl-config__label">Dimensions</label>
          <p class="sl-config__help">Select whole and fractional inches for width and height. Price updates based on your measurements.</p>
          <div class="sl-config__dims sl-config__dims--select">${inner}</div>
          <p class="sl-config__error" role="alert"></p>
        </div>`;
      }

      ['width', 'height', 'depth'].forEach((key) => {
        if (!d[key]) return;
        const dim = d[key];
        inner += `<div class="sl-config__dim">
          <label for="shade-${key}-${sid}">${escapeHtml(dim.label || key)}</label>
          <input type="number" id="shade-${key}-${sid}" data-dim="${key}"
            min="${dim.min}" max="${dim.max}" step="${dim.step || 0.125}"
            placeholder="${key === 'depth' ? 'Optional' : dim.min + '–' + dim.max}">
          <span class="sl-config__dim-unit">${unit}</span>
        </div>`;
      });
      return `<div class="sl-config__field" data-field-wrap="dimensions">
        <label class="sl-config__label">Dimensions <span class="sl-config__req">*</span></label>
        <p class="sl-config__help">Enter your window measurements in ${unit}.</p>
        <div class="sl-config__dims">${inner}</div>
        <p class="sl-config__error" role="alert"></p>
      </div>`;
    }

    syncDimensionTotals() {
      if (!this.usesWholeFractionDims()) return;
      ['width', 'height'].forEach((axis) => {
        const whole = this.state[axis + '_whole'];
        const frac = this.state[axis + '_frac'];
        if (whole === '' || frac === '') {
          this.state[axis] = '';
          return;
        }
        this.state[axis] = parseFloat(whole) + parseFloat(frac);
      });
    }

    formatDimensionValue(axis) {
      const whole = this.state[axis + '_whole'];
      const frac = this.state[axis + '_frac'];
      if (whole === '' || frac === '') return '';
      const fracLabel = FRACTION_OPTIONS.find((f) => f.value === frac)?.label || frac;
      if (frac === '0' || parseFloat(frac) === 0) return String(whole);
      return whole + ' ' + fracLabel;
    }

    renderInlineDimensionGroup(unit) {
      const sid = this.sectionId;
      const cell = (fieldId, label, axis, part, required) => {
        const req = required ? '<span class="sl-config__req">*</span>' : '';
        let options = '';
        if (part === 'whole') {
          if (this.usesApoInchPricing()) options = this.renderApoWholeOptions(axis);
          else {
            const dim = this.config.dimensions?.[axis] || {};
            options = this.renderWholeInchOptions(dim.min || 16, dim.max || 120);
          }
        } else {
          options = this.renderFractionOptions();
        }
        const dataAttr = part === 'whole' ? `data-dim-whole="${axis}"` : `data-dim-frac="${axis}"`;
        return `<div class="sl-config__dim" data-field-wrap="${fieldId}">
          <label class="sl-config__dim-label" for="shade-${fieldId}-${sid}">${escapeHtml(label)} ${req}</label>
          <select id="shade-${fieldId}-${sid}" ${dataAttr} class="sl-config__dim-select">${options}</select>
          <p class="sl-config__error" role="alert"></p>
        </div>`;
      };

      const depthField = (this.config.fields || []).find((f) => f.type === 'dimension_input');
      const depthLabel = depthField?.label || 'Depth';
      const depthHtml = `<div class="sl-config__dim sl-config__dim--depth" data-field-wrap="depth">
        <label class="sl-config__dim-label" for="shade-depth-${sid}">${escapeHtml(depthLabel)}</label>
        <input type="number" id="shade-depth-${sid}" class="sl-config__dim-input" data-dim="depth"
          min="0" step="0.125" inputmode="decimal" autocomplete="off">
        <span class="sl-config__dim-unit">${escapeHtml(unit)}</span>
        <p class="sl-config__error" role="alert"></p>
      </div>`;

      return `<div class="sl-config__field sl-config__field--dims" data-field-wrap="dimensions">
        <div class="sl-config__dims-grid">
          <div class="sl-config__dim-row">
            ${cell('width_whole', 'Width (Whole Inches)', 'width', 'whole', true)}
            ${cell('width_fractional', 'Width (Fractional Inches)', 'width', 'frac', true)}
          </div>
          <div class="sl-config__dim-row">
            ${cell('height_whole', 'Height (Whole Inches)', 'height', 'whole', true)}
            ${cell('height_fractional', 'Height (Fractional Inches)', 'height', 'frac', true)}
          </div>
        </div>
        ${depthHtml}
      </div>`;
    }

    renderField(field) {
      if (field.type === 'dimension_select' || field.type === 'dimension_input') return '';

      const req = field.required ? '<span class="sl-config__req">*</span>' : '';
      const swatchSize = this.config.swatch_size || 50;
      let help = '';
      if (field.help_html) {
        help = `<div class="sl-config__help">${field.help_html}</div>`;
      } else if (field.help_text || field.learn_more_url) {
        help = `<p class="sl-config__help">${escapeHtml(field.help_text || '')}`;
        if (field.learn_more_url) {
          help += ' <a href="' + escapeHtml(field.learn_more_url) + '">' + escapeHtml(field.learn_more_label || 'How To Measure') + '</a>';
        }
        help += '</p>';
      }

      let control = '';
      if (field.type === 'pill') {
        control = `<div class="sl-config__pills" role="group">${(field.choices || [])
          .map((c) => {
            const tip = c.tooltip
              ? ` title="${escapeHtml(String(c.tooltip).replace(/<[^>]*>/g, ''))}"`
              : '';
            return `<button type="button" class="sl-config__pill${c.default ? ' is-selected' : ''}" data-pill data-field="${field.id}" data-value="${escapeHtml(c.value)}"${tip}>${escapeHtml(c.label)}</button>`;
          })
          .join('')}</div>`;
      } else if (field.type === 'swatch') {
        const cols = field.swatch_cols ? ` style="--swatch-cols:${field.swatch_cols}"` : '';
        control = `<div class="sl-config__swatches"${cols} role="group">${(field.choices || [])
          .map((c) => {
            const galleryIdxAttr = c.gallery_index != null ? ` data-gallery-index="${c.gallery_index}"` : '';
            const img = c.image
              ? `<img src="${escapeHtml(c.image)}" alt="${escapeHtml(c.label)}" width="${swatchSize}" height="${swatchSize}" loading="lazy">`
              : `<span class="sl-config__swatch-inner" style="${c.color ? 'background-color:' + c.color : ''}"></span>`;
            const priceTag = c.price > 0 ? ` (+${formatMoney(c.price, this.currency)})` : '';
            return `<div class="sl-config__swatch-wrap"><button type="button" class="sl-config__swatch${c.default ? ' is-selected' : ''}" data-swatch data-field="${field.id}" data-value="${escapeHtml(c.value)}"${galleryIdxAttr} title="${escapeHtml(c.label)}${priceTag}" style="--swatch-size:${swatchSize}px">${img}</button><div class="sl-config__swatch-label">${escapeHtml(c.label)}${priceTag ? '<br>' + escapeHtml(priceTag.trim()) : ''}</div></div>`;
          })
          .join('')}</div>`;
      } else if (field.type === 'icon') {
        control = `<div class="sl-config__icons" role="group">${(field.choices || [])
          .map((c) => {
            const icon = MOUNT_ICONS[c.value] || (c.image ? `<img src="${c.image}" alt="" width="64" height="48">` : '');
            return `<button type="button" class="sl-config__icon-btn${c.default ? ' is-selected' : ''}" data-icon-choice data-field="${field.id}" data-value="${escapeHtml(c.value)}"><div class="sl-config__icon-img">${icon}</div><div class="sl-config__icon-label">${escapeHtml(c.label)}</div></button>`;
          })
          .join('')}</div>`;
      } else if (field.type === 'text') {
        control = `<input type="text" class="sl-config__text" data-text-field="${field.id}" placeholder="${escapeHtml(field.placeholder || '')}" maxlength="1000">`;
      }

      return `<div class="sl-config__field" data-field-wrap="${field.id}">
        <label class="sl-config__label">${escapeHtml(field.label)} ${req}</label>
        ${help}${control}
        <p class="sl-config__error" role="alert"></p>
      </div>`;
    }

    bindEvents() {
      this.configContainer.querySelectorAll('[data-pill]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.selections[btn.dataset.field] = btn.dataset.value;
          this.syncPills(btn.dataset.field);
          this.onFieldSelectionChange(btn.dataset.field);
          this.updatePrice();
          this.clearFieldError(btn.dataset.field);
        });
      });
      this.configContainer.querySelectorAll('[data-swatch]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.selections[btn.dataset.field] = btn.dataset.value;
          this.syncSwatches(btn.dataset.field);
          this.onFieldSelectionChange(btn.dataset.field, btn);
          this.updatePrice();
          this.clearFieldError(btn.dataset.field);
        });
      });
      this.configContainer.querySelectorAll('[data-icon-choice]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.selections[btn.dataset.field] = btn.dataset.value;
          this.syncIcons(btn.dataset.field);
          this.onFieldSelectionChange(btn.dataset.field);
          this.updatePrice();
          this.clearFieldError(btn.dataset.field);
        });
      });
      ['width', 'height', 'depth'].forEach((dim) => {
        const input = this.configContainer.querySelector('[data-dim="' + dim + '"]');
        if (!input) return;
        input.addEventListener('input', () => {
          this.state[dim] = input.value;
          this.updatePrice();
          if (dim === 'depth') this.clearFieldError('depth');
          else this.clearFieldError('dimensions');
        });
      });
      this.configContainer.querySelectorAll('[data-dim-whole]').forEach((sel) => {
        sel.addEventListener('change', () => {
          const axis = sel.dataset.dimWhole;
          this.state[axis + '_whole'] = sel.value;
          this.syncDimensionTotals();
          this.updatePrice();
          const wrap = sel.closest('[data-field-wrap]');
          if (wrap) this.clearFieldError(wrap.dataset.fieldWrap);
          else this.clearFieldError('dimensions');
        });
      });
      this.configContainer.querySelectorAll('[data-dim-frac]').forEach((sel) => {
        sel.addEventListener('change', () => {
          const axis = sel.dataset.dimFrac;
          this.state[axis + '_frac'] = sel.value;
          this.syncDimensionTotals();
          this.updatePrice();
          const wrap = sel.closest('[data-field-wrap]');
          if (wrap) this.clearFieldError(wrap.dataset.fieldWrap);
          else this.clearFieldError('dimensions');
        });
      });
      this.configContainer.querySelectorAll('[data-text-field]').forEach((input) => {
        input.addEventListener('input', () => {
          this.selections[input.dataset.textField] = input.value.trim();
        });
      });
      const qtyMinus = this.root.querySelector('[data-qty-minus]');
      const qtyPlus = this.root.querySelector('[data-qty-plus]');
      const qtyInput = this.root.querySelector('[data-qty-input]');
      if (qtyMinus && qtyPlus && qtyInput) {
        qtyMinus.addEventListener('click', () => {
          qtyInput.value = Math.max(1, parseInt(qtyInput.value, 10) - 1);
        });
        qtyPlus.addEventListener('click', () => {
          qtyInput.value = parseInt(qtyInput.value, 10) + 1;
        });
      }
    }

    setDefaults() {
      (this.config.fields || []).forEach((field) => {
        if (field.type === 'text') return;
        if (field.type === 'dimension_select') return;
        const def = (field.choices || []).find((c) => c.default);
        if (def) this.selections[field.id] = def.value;
      });
      this.syncDimensionTotals();
    }

    applyDimensionDefaults() {
      ['width', 'height'].forEach((axis) => {
        const whole = this.state[axis + '_whole'];
        const frac = this.state[axis + '_frac'];
        if (whole !== '') {
          const sel = this.configContainer.querySelector('[data-dim-whole="' + axis + '"]');
          if (sel) sel.value = whole;
        }
        if (frac !== '') {
          const sel = this.configContainer.querySelector('[data-dim-frac="' + axis + '"]');
          if (sel) sel.value = frac;
        }
      });
    }

    getGalleryFieldId() {
      if (this.config.gallery_field) return this.config.gallery_field;
      const swatch = (this.config.fields || []).find((f) => f.type === 'swatch');
      return swatch ? swatch.id : null;
    }

    getChoice(fieldId, value) {
      const field = (this.config.fields || []).find((f) => f.id === fieldId);
      if (!field) return null;
      return (field.choices || []).find((c) => c.value === value) || null;
    }

    getChoiceImage(fieldId, value) {
      return this.getChoice(fieldId, value)?.image || null;
    }

    showGalleryForChoice(choice) {
      if (!choice || !this.gallery) return;
      if (choice.gallery_index != null) {
        this.gallery.showIndex(choice.gallery_index);
      } else if (choice.image) {
        this.gallery.showUrl(choice.image);
      }
    }

    onFieldSelectionChange(fieldId, triggerEl) {
      if (fieldId !== this.getGalleryFieldId()) return;
      const choice = this.getChoice(fieldId, this.selections[fieldId]);
      if (triggerEl?.dataset?.galleryIndex != null) {
        this.gallery?.showIndex(parseInt(triggerEl.dataset.galleryIndex, 10));
        return;
      }
      this.showGalleryForChoice(choice);
    }

    updateGalleryFromSelection() {
      const fieldId = this.getGalleryFieldId();
      if (!fieldId) return;
      this.showGalleryForChoice(this.getChoice(fieldId, this.selections[fieldId]));
    }

    syncPills(fieldId) {
      this.configContainer.querySelectorAll('[data-pill][data-field="' + fieldId + '"]').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.value === this.selections[fieldId]);
      });
    }

    syncSwatches(fieldId) {
      this.configContainer.querySelectorAll('[data-swatch][data-field="' + fieldId + '"]').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.value === this.selections[fieldId]);
      });
    }

    syncIcons(fieldId) {
      this.configContainer.querySelectorAll('[data-icon-choice][data-field="' + fieldId + '"]').forEach((btn) => {
        btn.classList.toggle('is-selected', btn.dataset.value === this.selections[fieldId]);
      });
    }

    getApoInchAddons() {
      let total = 0;
      if (this.state.width_whole !== '') {
        const w = (this.config.width_whole_options || []).find(
          (o) => String(o.inch) === String(this.state.width_whole)
        );
        total += w?.addon || 0;
      }
      if (this.state.height_whole !== '') {
        const h = (this.config.height_whole_options || []).find(
          (o) => String(o.inch) === String(this.state.height_whole)
        );
        total += h?.addon || 0;
      }
      return total;
    }

    getDimensionPrice() {
      if (this.usesApoInchPricing()) {
        if (this.state.width_whole === '' || this.state.height_whole === '') return null;
        return (this.config.base_price || this.variantPrice || 17999) + this.getApoInchAddons();
      }
      const w = parseFloat(this.state.width);
      const h = parseFloat(this.state.height);
      if (isNaN(w) || isNaN(h)) return null;
      for (const row of this.config.dimension_pricing || []) {
        if (w >= row.w_min && w <= row.w_max && h >= row.h_min && h <= row.h_max) return row.price;
      }
      return null;
    }

    getOptionsPrice() {
      let total = 0;
      (this.config.fields || []).forEach((field) => {
        if (!field.choices) return;
        const choice = field.choices.find((c) => c.value === this.selections[field.id]);
        if (choice?.price) total += choice.price;
      });
      return total;
    }

    getMinDimensionPrice() {
      if (this.usesApoInchPricing()) return this.config.base_price || this.variantPrice || 17999;
      const rows = this.config.dimension_pricing || [];
      if (!rows.length) return this.variantPrice;
      return Math.min(...rows.map((r) => r.price));
    }

    calculateTotalCents() {
      if (this.usesApoInchPricing()) {
        const base = this.config.base_price || this.variantPrice || 17999;
        return base + this.getApoInchAddons() + this.getOptionsPrice();
      }
      const dimPrice = this.getDimensionPrice();
      const base = dimPrice !== null ? dimPrice : (this.variantPrice || this.getMinDimensionPrice());
      return base + this.getOptionsPrice();
    }

    updatePrice() {
      const total = this.calculateTotalCents();
      if (this.priceEl) this.priceEl.textContent = formatMoney(total, this.currency);
      const shopPay = this.root.querySelector('.sl-pdp__shop-pay');
      if (shopPay) {
        const perMonth = (total / 100 / 12).toFixed(2);
        shopPay.innerHTML =
          '4 interest-free installments, or from <strong>$' + perMonth + '/mo</strong> with Shop Pay';
      }
    }

    validate() {
      let valid = true;
      this.configContainer.querySelectorAll('.sl-config__field').forEach((el) => el.classList.remove('has-error'));
      const dims = this.config.dimensions || {};
      const unit = this.config.dimension_unit || 'in';
      const inlineDims = this.hasInlineDimensionFields();

      const wMin = this.usesApoInchPricing()
        ? this.config.width_whole_options?.[0]?.inch
        : dims.width?.min;
      const wMax = this.usesApoInchPricing()
        ? this.config.width_whole_options?.[this.config.width_whole_options.length - 1]?.inch
        : dims.width?.max;
      const hMin = this.usesApoInchPricing()
        ? this.config.height_whole_options?.[0]?.inch
        : dims.height?.min;
      const hMax = this.usesApoInchPricing()
        ? this.config.height_whole_options?.[this.config.height_whole_options.length - 1]?.inch
        : dims.height?.max;

      if (inlineDims) {
        (this.config.fields || []).forEach((field) => {
          if (field.type !== 'dimension_select' || !field.required) return;
          const axis = field.axis;
          const part = field.part;
          const val = part === 'whole' ? this.state[axis + '_whole'] : this.state[axis + '_frac'];
          if (val === '') {
            this.showFieldError(field.id, 'Please make a selection');
            valid = false;
          }
        });
        if (this.state.width_whole !== '' && this.state.width_frac !== '') {
          const w = parseFloat(this.state.width);
          if (!isNaN(w) && (w < wMin || w > wMax)) {
            this.showFieldError('width_whole', `Width must be between ${wMin} and ${wMax} ${unit}`);
            valid = false;
          }
        }
        if (this.state.height_whole !== '' && this.state.height_frac !== '') {
          const h = parseFloat(this.state.height);
          if (!isNaN(h) && (h < hMin || h > hMax)) {
            this.showFieldError('height_whole', `Height must be between ${hMin} and ${hMax} ${unit}`);
            valid = false;
          }
        }
      } else {
        if (dims.width?.required) {
          if (this.usesWholeFractionDims()) {
            if (this.state.width_whole === '' || this.state.width_frac === '') {
              this.showFieldError('dimensions', 'Please select width (whole and fractional inches)');
              valid = false;
            } else {
              const w = parseFloat(this.state.width);
              if (isNaN(w) || w < wMin || w > wMax) {
                this.showFieldError('dimensions', `Width must be between ${wMin} and ${wMax} ${unit}`);
                valid = false;
              }
            }
          } else {
            const w = parseFloat(this.state.width);
            if (isNaN(w) || w < dims.width.min || w > dims.width.max) {
              this.showFieldError('dimensions', `Please enter a valid width (${dims.width.min}–${dims.width.max} ${unit})`);
              valid = false;
            }
          }
        }
        if (dims.height?.required) {
          if (this.usesWholeFractionDims()) {
            if (this.state.height_whole === '' || this.state.height_frac === '') {
              this.showFieldError('dimensions', 'Please select height (whole and fractional inches)');
              valid = false;
            } else {
              const h = parseFloat(this.state.height);
              if (isNaN(h) || h < hMin || h > hMax) {
                this.showFieldError('dimensions', `Height must be between ${hMin} and ${hMax} ${unit}`);
                valid = false;
              }
            }
          } else {
            const h = parseFloat(this.state.height);
            if (isNaN(h) || h < dims.height.min || h > dims.height.max) {
              this.showFieldError('dimensions', `Please enter a valid height (${dims.height.min}–${dims.height.max} ${unit})`);
              valid = false;
            }
          }
        }
      }

      if (
        !this.usesApoInchPricing() &&
        this.state.width &&
        this.state.height &&
        this.getDimensionPrice() === null
      ) {
        this.showFieldError('dimensions', 'No pricing available for this size. Please contact us.');
        valid = false;
      }

      (this.config.fields || []).forEach((field) => {
        if (!field.required) return;
        if (field.type === 'dimension_select' || field.type === 'text') return;
        if (!this.selections[field.id]) {
          this.showFieldError(field.id, 'Please make a selection');
          valid = false;
        }
      });
      return valid;
    }

    showFieldError(fieldId, msg) {
      const el = this.configContainer.querySelector('[data-field-wrap="' + fieldId + '"]');
      if (!el) return;
      el.classList.add('has-error');
      const err = el.querySelector('.sl-config__error');
      if (err) err.textContent = msg;
    }

    clearFieldError(fieldId) {
      const el = this.configContainer.querySelector('[data-field-wrap="' + fieldId + '"]');
      if (el) el.classList.remove('has-error');
    }

    buildProperties() {
      const props = {};
      const unit = this.config.dimension_unit || 'in';
      if (this.hasInlineDimensionFields() || this.usesWholeFractionDims()) {
        if (this.state.width_whole !== '' && this.state.width_frac !== '') {
          props['Width (' + unit + ')'] = this.formatDimensionValue('width');
        }
        if (this.state.height_whole !== '' && this.state.height_frac !== '') {
          props['Height (' + unit + ')'] = this.formatDimensionValue('height');
        }
      } else {
        if (this.state.width) props['Width (' + unit + ')'] = this.state.width;
        if (this.state.height) props['Height (' + unit + ')'] = this.state.height;
      }
      if (this.state.depth) props['Depth (' + unit + ')'] = this.state.depth;
      (this.config.fields || []).forEach((field) => {
        const val = this.selections[field.id];
        if (!val) return;
        if (field.type === 'text') {
          props[field.label] = val;
          return;
        }
        const choice = (field.choices || []).find((c) => c.value === val);
        props[field.label] = choice ? choice.label : val;
      });
      props['Configured Price'] = formatMoney(this.calculateTotalCents(), this.currency);
      return props;
    }

    onSubmit(e) {
      e.preventDefault();
      const errBox = this.root.querySelector('[data-form-error]');
      errBox?.classList.remove('is-visible');
      if (!this.validate()) return;

      const btn = this.form.querySelector('[type="submit"]');
      btn.disabled = true;
      btn.classList.add('loading');

      const formData = new FormData(this.form);
      Object.entries(this.buildProperties()).forEach(([k, v]) => {
        formData.append('properties[' + k + ']', v);
      });

      fetch(window.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.status) {
            if (errBox) {
              errBox.textContent = data.description || data.message || 'Could not add to cart';
              errBox.classList.add('is-visible');
            }
            return;
          }
          window.location = window.routes?.cart_url || '/cart';
        })
        .catch(() => {
          if (errBox) {
            errBox.textContent = 'Something went wrong. Please try again.';
            errBox.classList.add('is-visible');
          }
        })
        .finally(() => {
          btn.disabled = false;
          btn.classList.remove('loading');
        });
    }
  }

  function initGallery(root) {
    const main = root.querySelector('[data-gallery-main]');
    const thumbs = root.querySelectorAll('[data-gallery-thumb]');
    const counter = root.querySelector('[data-gallery-counter]');
    const zoomBtn = root.querySelector('[data-gallery-zoom]');
    if (!main || !thumbs.length) return null;
    const images = [];
    thumbs.forEach((t) => images.push(t.dataset.fullSrc || t.querySelector('img')?.src));
    let idx = parseInt(root.dataset.galleryStartIndex, 10) || 0;

    function syncThumbs(activeIdx) {
      thumbs.forEach((t, j) => t.classList.toggle('is-active', j === activeIdx));
      if (counter) counter.textContent = activeIdx + 1 + ' / ' + images.length;
    }

    function showIndex(i) {
      idx = (i + images.length) % images.length;
      if (images[idx]) {
        main.src = images[idx];
        syncThumbs(idx);
      }
    }

    thumbs.forEach((t, i) => t.addEventListener('click', () => showIndex(i)));
    root.querySelector('[data-gallery-prev]')?.addEventListener('click', () => showIndex(idx - 1));
    root.querySelector('[data-gallery-next]')?.addEventListener('click', () => showIndex(idx + 1));
    zoomBtn?.addEventListener('click', () => {
      if (main.src) window.open(main.src, '_blank', 'noopener');
    });

    showIndex(idx);

    return {
      showIndex,
      showUrl(url) {
        if (!url) return;
        const matchIdx = images.findIndex((img) => img === url || img.split('?')[0] === url.split('?')[0]);
        if (matchIdx >= 0) showIndex(matchIdx);
        else {
          main.src = url;
          thumbs.forEach((t) => t.classList.remove('is-active'));
        }
      },
    };
  }

  function bootShadeConfigurator() {
    document.querySelectorAll('[data-shade-configurator]').forEach((root) => {
      const gallery = initGallery(root);
      new ShadeConfigurator(root, gallery);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootShadeConfigurator);
  } else {
    bootShadeConfigurator();
  }
})();


/* Softlight: Globo options integration for blackout roller */
(function () {
  const rootSelector = '[data-default-config-url*="shade-config-linen-blackout-roller.json"]';
  const numberFromMoney = function (text) {
    const value = Number.parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(value) ? value : 0;
  };
  const init = function () {
    const root = document.querySelector(rootSelector);
    if (!root || root.dataset.softlightGpoReady === 'true') return Boolean(root);
    const price = root.querySelector('.sl-pdp__price');
    if (!price) return false;
    root.dataset.softlightGpoReady = 'true';
    const basePrice = numberFromMoney(price.textContent);
    const updatePrice = function () {
      const addOn = numberFromMoney(root.querySelector('.gpo-total-text .money')?.textContent);
      const nextText = '$' + (basePrice + addOn).toFixed(2) + ' USD';
      if (price.textContent.trim() !== nextText) price.textContent = nextText;
    };
    root.addEventListener('change', function () { window.setTimeout(updatePrice, 0); }, true);
    new MutationObserver(updatePrice).observe(root, { subtree: true, childList: true, characterData: true });
    updatePrice();
    return true;
  };
  if (!document.getElementById('softlight-gpo-blackout-style')) {
    const style = document.createElement('style');
    style.id = 'softlight-gpo-blackout-style';
    style.textContent = rootSelector + ' .gpo-swatch__inner img{width:110px!important;height:110px!important;object-fit:cover!important;}' + rootSelector + ' .gpo-element[data-element-id="textarea-1"] textarea{height:56px!important;min-height:56px!important;resize:vertical;}';
    document.head.appendChild(style);
  }
  if (!init()) {
    let attempts = 0;
    const timer = window.setInterval(function () { attempts += 1; if (init() || attempts >= 40) window.clearInterval(timer); }, 500);
  }
})();

/* Softlight: high-resolution option images and larger mount cards */
(function () {
  const styleId = 'softlight-globo-image-quality-v2';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent =
      'label[for^="image-swatches-2_"],label[for^="image-swatches-2_"] .gpo-swatch__inner{width:180px!important;height:180px!important;}' +
      'label[for^="image-swatches-2_"] img.swatch-image{width:180px!important;height:180px!important;object-fit:contain!important;background:#fff!important;}' +
      '@media(max-width:749px){label[for^="image-swatches-2_"],label[for^="image-swatches-2_"] .gpo-swatch__inner{width:145px!important;height:145px!important;}label[for^="image-swatches-2_"] img.swatch-image{width:145px!important;height:145px!important;}}';
    document.head.appendChild(style);
  }

  function upgradeOptionImages() {
    document.querySelectorAll('img.swatch-image').forEach(function (img) {
      try {
        const url = new URL(img.currentSrc || img.src, window.location.href);
        const width = Number(url.searchParams.get('width') || 0);
        if (width && width < 360) {
          url.searchParams.set('width', '480');
          img.src = url.toString();
          img.removeAttribute('srcset');
        }
      } catch (error) {}
    });
  }

  upgradeOptionImages();
  const observer = new MutationObserver(upgradeOptionImages);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();


/* softlight-fabric-scene-link-v1 */
(function () {
  var fabricScenes = {
    RB001: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB001-scene.png',
    RB002: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB002-scene.png',
    RB003: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB003-scene.png',
    RB004: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB004-scene.png',
    RB005: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB005-scene.png',
    RB006: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB006-scene.png',
    RB007: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB007-scene.png',
    RB008: 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/RB008-scene.png'
  };

  function showFabricScene(value) {
    var key = String(value || '').trim().toUpperCase();
    var src = fabricScenes[key];
    if (!src) return;
    var root = document.querySelector('[data-shade-configurator]');
    var main = root && root.querySelector('[data-gallery-main]');
    if (!main) return;
    main.src = src;
    main.alt = key + ' fabric scene';
    main.removeAttribute('srcset');
    root.querySelectorAll('[data-gallery-thumb]').forEach(function (button) {
      button.classList.remove('is-active');
    });
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || !target.matches('input[name="Color"], input[data-field-name="Color"]')) return;
    showFabricScene(target.value);
  });

  document.addEventListener('click', function (event) {
    var label = event.target.closest && event.target.closest('label[for^="image-swatches-1_"]');
    if (!label) return;
    var input = document.getElementById(label.getAttribute('for'));
    if (input) setTimeout(function () { showFabricScene(input.value); }, 0);
  });
})();


/* softlight-visual-link-v2 */
(function () {
  var cdn = 'https://cdn.shopify.com/s/files/1/0633/1645/9579/files/';
  function sceneFor(value) { return /^RB00[1-8]$/.test(value || '') ? cdn + value + '-scene.png' : ''; }
  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-swatch][data-field="color"]');
    if (!button) return;
    var src = sceneFor(button.getAttribute('data-value'));
    var main = document.querySelector('[data-shade-configurator] [data-gallery-main]');
    if (src && main) {
      main.src = src + '?width=2200';
      main.removeAttribute('srcset');
      main.alt = button.getAttribute('data-value') + ' blackout shade scene';
      document.querySelectorAll('[data-gallery-thumb]').forEach(function (thumb) { thumb.classList.remove('is-active'); });
    }
  });
  function deliveryWindow() {
    var el = document.querySelector('[data-delivery-window]');
    if (!el) return;
    var isFauxWood = window.location.pathname.indexOf('/products/faux-wood-blinds') !== -1;
    var start = new Date(); start.setDate(start.getDate() + (isFauxWood ? 7 : 12));
    var end = new Date(); end.setDate(end.getDate() + (isFauxWood ? 9 : 18));
    var fmt = new Intl.DateTimeFormat('en-US', { month:'short', day:'numeric' });
    el.textContent = 'Arrives ' + fmt.format(start) + ' – ' + fmt.format(end);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', deliveryWindow); else deliveryWindow();
})();
